import React, { Key, useCallback, useEffect, useRef, useState } from "react";

import _ from "lodash";
import { queryCache } from "react-query";

import {
  VERO1_QUERY_KEYS,
  useAddVero1ChecklistItem,
  useDeleteVero1ChecklistItem,
  useAutosaveVero1ItemEdit,
  useCarryForwardVero1Checklist,
  useDiscardVero1Checklist,
  useGetVero1Checklist,
  useRefreshVero1Checklist,
  enterVero1Settling,
  exitVero1Settling,
  isRunInProgressConflict,
  useStartVero1Checklist,
} from "manager/hooks/api/vero1Queries";

import type { AutosaveEntitySnapshot } from "shared/autosave";
import { Button } from "shared/components/Button";
import FlexContainer from "shared/components/FlexContainer";
import { useHideMessenger } from "shared/components/Messenger/useHideMessenger";
import Spacer from "shared/components/Spacer";
import { RegularText, SmallText } from "shared/components/Typography";
import { NOTIFICATIONS } from "shared/config/constants";
import * as Icon from "shared/icons/icons";
import type {
  Vero1Checklist,
  Vero1ChecklistItem,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import {
  Vero1ChecklistStatus,
  Vero1GenerationStatus,
} from "shared/interfaces/api/vero1";

import { openNotification } from "shared/utils/ui";

import { formatReviewTime } from "../vero1Dates";

import Vero1ErrorBoundary from "../Vero1ErrorBoundary";

import { AddChecklistItem } from "./AddChecklistItem";
import type { Vero1AutosaveFields } from "./autosaveQueue";
import {
  disposeVero1AutosaveQueue,
  getVero1AutosaveQueue,
} from "./autosaveQueue";
import { AUTOSAVE_STRINGS, autosaveTimeLabel } from "./autosaveStrings";
import { ChecklistRow } from "./ChecklistRow";
import { ChecklistRowEditor } from "./ChecklistRowEditor";
import type { ClearedEntry } from "./clearedByRefresh";
import {
  clearedByRefresh,
  readClearedEntries,
  storeClearedEntries,
} from "./clearedByRefresh";
import {
  PANEL_WIDTH_DEFAULT,
  PANEL_WIDTH_MAX,
  PANEL_WIDTH_MIN,
} from "./constants";
import { useVero1ReviewPanel } from "./context";
import { categorySortKey, formatCategory } from "./formatItemValue";
import {
  BangGlyph,
  ChevronRightGlyph,
  CrossGlyph,
  SpinnerGlyph,
  TickGlyph,
} from "./icons";
import {
  hasResultMoved,
  isAssessable,
  isIncomplete,
  isLeasingTouched,
  needsReason,
} from "./itemState";
import { orderRowsInGroup } from "./orderRows";
import { Vero1ConflictCode } from "./persistItem";
import { RefreshClearedNotice } from "./RefreshClearedNotice";
import { getVero1ReportAutosaveQueue, REPORT_ENTITY } from "./reportAutosave";
import { SaveStatus } from "./reportStyled";
import { ReviewReportStep } from "./ReviewReportStep";
import {
  ActivityStrip,
  ActivityStripDetail,
  ScreeningDetail,
  ScreeningSpinHolder,
  ScreeningStrip,
  ScreeningTitle,
  CarryForwardBanner,
  CategoryLabel,
  CloseButton,
  DiscardLink,
  FooterHelper,
  FooterRow,
  DockedPane,
  LumenSummaryBlock,
  LumenSummaryDetail,
  LumenSummaryTitle,
  VerdictCircle,
  DecisionedBanner,
  DraftBadge,
  EmptyState,
  PanelAttribution,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PanelNoticeSlot,
  PanelPrimaryButton,
  PanelSecondaryButton,
  PanelTitle,
  RailDot,
  ResizeGrip,
  SkeletonBody,
  SkeletonDisc,
  SkeletonLine,
  SkeletonLines,
  SkeletonRow,
  SkeletonTitle,
} from "./styled";

export interface Vero1ReviewPanelProps {
  dealId: Key;
  /** Unit/property identity shown in the header so the agent always knows
   *  which deal the review belongs to. */
  dealLabel?: string | null;
  visible: boolean;
  onClose: () => void;
  /** Ends the session outright — no rail is left offering to reopen a panel
   *  with no draft behind it. */
  onExit?: () => void;
  /** The trigger's start is in flight: render the skeleton, not the prior
   *  record — its Start button would offer the same act a second time. */
  startPending?: boolean;
}

/**
 * The VERO1 agent review panel (PRODUCT-2059).
 *
 * Docked pane, not an overlay: the page content sits beside it so the agent
 * keeps cross-referencing the deal while reviewing. One width (spec §1.2);
 * Minimize collapses it to the rail (Vero1ReviewHandle). All geometry lives
 * in ./constants.
 */

/* Full-height checklist skeleton (drawing 6.1) — enough row groups to fill
   the panel's length, with varied widths so it reads as the rows to come. */
const SKELETON_ROWS: Array<{ id: string; title?: number; lines: string[] }> = [
  { id: "a", lines: ["100%", "78%"] },
  { id: "b", title: 96, lines: ["92%", "54%"] },
  { id: "c", title: 120, lines: ["86%", "64%"] },
  { id: "d", lines: ["95%", "40%"] },
  { id: "e", title: 104, lines: ["88%", "70%"] },
  { id: "f", title: 88, lines: ["82%", "48%"] },
];

const ChecklistSkeleton: React.FC<{ testId: string }> = ({ testId }) => (
  <SkeletonBody data-testid={testId}>
    {SKELETON_ROWS.map((row) => (
      <React.Fragment key={row.id}>
        <SkeletonRow>
          <SkeletonDisc />
          <SkeletonTitle $w={row.title} />
        </SkeletonRow>
        <SkeletonLines>
          {row.lines.map((width) => (
            <SkeletonLine
              key={width}
              $w={width === "100%" ? undefined : width}
            />
          ))}
        </SkeletonLines>
      </React.Fragment>
    ))}
  </SkeletonBody>
);

const PanelBodyContent = ({ render }: { render: () => React.ReactNode }) => (
  <>{render()}</>
);

export const Vero1ReviewPanel = ({
  dealId,
  dealLabel,
  visible,
  onClose,
  onExit,
  startPending,
}: Vero1ReviewPanelProps) => {
  const {
    checklist,
    generation,
    reasonCatalog,
    persistScopeOptions,
    canAddField,
    baseRunStale,
    isChecklistLoading,
  } = useGetVero1Checklist(
    dealId,
    visible,
    // the open panel polls: a run landing while the agent works must be
    // noticed without any agent action — the staleness flag only rides on
    // fetches, so a settled panel that never asks never learns
    true
  );
  // a decision taken while a draft was open closes the review: the backend
  // refuses every write from that moment, so the panel must stop offering
  // them rather than collecting work that can only fail on save. A refused
  // write says so a full round-trip before the refetch does, and the agent
  // should not keep typing in the meantime
  const [writeRefused, setWriteRefused] = useState(false);
  const readOnly =
    writeRefused || generation?.state === Vero1GenerationStatus.DealDecisioned;
  const { refreshChecklist, isRefreshingChecklist } =
    useRefreshVero1Checklist(dealId);

  const editItemSlim = useAutosaveVero1ItemEdit(dealId);
  const { addItem, isAddingItem } = useAddVero1ChecklistItem(dealId);
  const { deleteItem, isDeletingItem } = useDeleteVero1ChecklistItem(dealId);
  // a custom row removal takes an explicit confirm step (destructive,
  // recorded) — inline on the row, like discard
  const [removingItemId, setRemovingItemId] = useState<Key | null>(null);
  const { carryForward, isCarryingForward } =
    useCarryForwardVero1Checklist(dealId);

  // the support launcher sits over the footer once the pane is widened
  useHideMessenger(visible);

  const [width, setWidth] = useState(PANEL_WIDTH_DEFAULT);
  const paneRef = useRef<HTMLElement | null>(null);
  // one active drag at a time; cancelled gestures and unmounts both detach
  const dragCleanup = useRef<(() => void) | null>(null);

  /**
   * The pane itself must never scroll — PanelBody scrolls between a pinned
   * header and footer. overflow:hidden does not prevent PROGRAMMATIC scrolling:
   * focusing a control deep in the tall add-a-field form makes the browser
   * scroll the nearest scrollable ancestor to reveal it, and the pane qualifies.
   * It scrolled ~1368px, carrying the checklist to a negative offset where it
   * was clipped — rows still in the DOM, but invisible and, with no scrollbar,
   * impossible to scroll back. Pin it to 0 whenever anything moves it.
   */
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return undefined;
    const pin = () => {
      if (pane.scrollTop !== 0) pane.scrollTop = 0;
      if (pane.scrollLeft !== 0) pane.scrollLeft = 0;
    };
    pane.addEventListener("scroll", pin, { passive: true });
    return () => pane.removeEventListener("scroll", pin);
  }, [visible]);
  useEffect(() => () => dragCleanup.current?.(), []);
  const startResize = useCallback((event: React.PointerEvent) => {
    event.preventDefault();
    dragCleanup.current?.();
    const onMove = (move: PointerEvent) => {
      const right = paneRef.current?.getBoundingClientRect().right;
      if (right === undefined) return;
      setWidth(
        Math.min(
          PANEL_WIDTH_MAX,
          Math.max(PANEL_WIDTH_MIN, right - move.clientX)
        )
      );
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanup.current = null;
    };
    dragCleanup.current = onUp;
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, []);
  // checklist → report → (confirm, inside the step). Only a draft can be
  // reported on — discard or submit drops the panel back to the checklist
  const [onReportStep, setOnReportStep] = useState(false);

  // once per draft: dismissing or running the carry-forward retires the offer
  const [carryForwardDismissed, setCarryForwardDismissed] = useState(false);
  const checklistId = checklist?.id ?? null;
  useEffect(() => {
    setCarryForwardDismissed(false);
    // the report step belongs to the draft that opened it
    setOnReportStep(false);
  }, [checklistId]);
  const { discardChecklist, isDiscardingChecklist } =
    useDiscardVero1Checklist(dealId);
  const { startChecklist, isStartingChecklist } =
    useStartVero1Checklist(dealId);
  // the trigger owns the pending flag on the deal page; the footer's own
  // Start must raise it too, or the record it replaces stays interactive
  const {
    setStartPending,
    autoStartWhenReady,
    setAutoStartWhenReady,
    openReview,
  } = useVero1ReviewPanel();
  // a failed discard leaves a live draft; reopen the session the close tore down
  const onDiscardFailed = useCallback(
    () => openReview(dealId, dealLabel ?? undefined),
    [openReview, dealId, dealLabel]
  );

  // the base run moved under the open draft: refresh automatically — only
  // changed rows re-seed, unchanged entries are preserved (spec: no agent
  // action). Once per pinned run so a still-stale response can't loop.
  const refreshedForRun = useRef<string | null>(null);
  const staleRunId = checklist?.baseRunId ?? null;
  // rows the auto-refresh reset that the analyst had worked. The reset is
  // deliberate and promptless, so the panel must SAY it happened and hold the
  // cleared text — the API does not return what it discarded, so the pre-
  // refresh items are snapshotted at dispatch. Accumulated across refreshes
  // (an undismissed entry stays relevant); dismissal empties it, and so does
  // a new draft — the text belongs to the review it was cleared from. The
  // tab's sessionStorage mirrors the state so a reload cannot eat an
  // undismissed notice — the server has nothing to rebuild it from.
  const [clearedEntries, setClearedEntries] = useState<ClearedEntry[]>([]);
  useEffect(() => {
    if (checklist?.status === Vero1ChecklistStatus.Draft) {
      setClearedEntries(readClearedEntries(checklistId));
      return;
    }
    // the receipt belongs to the draft phase — a submitted or discarded
    // review keeps its id, and resurrecting "your entries were cleared" on
    // the finished record would announce a loss that no longer applies
    setClearedEntries([]);
    if (checklistId !== null) storeClearedEntries(checklistId, []);
  }, [checklistId, checklist?.status]);
  const dismissClearedNotice = () => {
    setClearedEntries([]);
    if (checklistId !== null) storeClearedEntries(checklistId, []);
  };
  // one row edits at a time — the editor opens in-row, so a second open would
  // push the first off screen mid-edit. The version token is captured at
  // editor OPEN: a refetch landing someone else's newer draft mid-edit must
  // make this save 409, not silently pass the version check. Declared here
  // because the auto-refresh below defers while an editor is open.
  const [editingItemId, setEditingItemId] = useState<Key | null>(null);

  // a deal decisioned mid-edit drops the open editor: leaving it mounted would
  // hold a draft that can no longer be saved, and the footer's "save or cancel
  // the item you are editing" gate would strand the agent
  useEffect(() => {
    if (!readOnly) return;
    // only shout when it lands mid-edit — opening an already-decided deal is
    // explained by the banner alone
    if (editingItemId !== null) {
      openNotification(
        "Leasing decided this deal — the review is now read-only. Your entries are kept, but nothing further can be saved.",
        NOTIFICATIONS.warning
      );
      setEditingItemId(null);
    }
  }, [readOnly, editingItemId]);
  useEffect(() => {
    if (
      baseRunStale &&
      staleRunId &&
      checklist?.status === Vero1ChecklistStatus.Draft &&
      !readOnly &&
      !isRefreshingChecklist &&
      // an open editor seeded its fields from the row as it was at open, and
      // re-seeding underneath it would silently move the baseline the save
      // diffs against — recording an override the agent never made. Deferred,
      // not cancelled: closing the editor re-runs this effect (spec keeps the
      // refresh agent-action-free, so nothing prompts here)
      editingItemId === null &&
      refreshedForRun.current !== staleRunId
    ) {
      refreshedForRun.current = staleRunId;
      const itemsBefore = checklist.items;
      refreshChecklist(undefined, {
        onSuccess: (
          fresh:
            | { items?: Vero1ChecklistItem[]; updatedAt?: string }
            | undefined
        ) => {
          queue?.engine.adoptTokenIfNewer(fresh?.updatedAt);
          const cleared = clearedByRefresh(itemsBefore, fresh?.items);
          // a flush must not resurrect what the reset dropped
          cleared.forEach((entry) => queue?.engine.drop(String(entry.itemId)));
          if (cleared.length) {
            setClearedEntries((held) => {
              const merged = held
                .filter((h) => !cleared.some((c) => c.itemId === h.itemId))
                .concat(cleared);
              if (checklistId !== null)
                storeClearedEntries(checklistId, merged);
              return merged;
            });
          }
        },
        // a conflict means the draft moved and the mutation helper already
        // refetches — releasing the guard lets the fresh state retry, each
        // attempt gated by a server round-trip. Other failures keep the
        // guard (the error toast showed) so a dead endpoint can't storm.
        onError: (error: {
          statusCode?: number;
          response?: { status?: number };
        }) => {
          const status = error?.statusCode ?? error?.response?.status;
          if (status === 409 || status === 404) {
            refreshedForRun.current = null;
          }
        },
      });
    }
  }, [
    baseRunStale,
    staleRunId,
    checklist?.status,
    checklist?.items,
    readOnly,
    isRefreshingChecklist,
    editingItemId,
    refreshChecklist,
  ]);

  // discard is destructive and recorded, so it takes an explicit second step.
  // Inline rather than a modal-on-drawer; designs will restyle (spec: confirm
  // dialog explaining it releases the leasing-decision block).
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [bodyErrored, setBodyErrored] = useState(false);

  // module-scoped by checklist (spec §09): the queue outlives this mount,
  // and a new draft gets a fresh queue by construction — a queue stopped by
  // a blocked write cannot haunt the next review on the deal
  const queue =
    checklistId !== null ? getVero1AutosaveQueue(checklistId) : null;
  // a 422 carries the row's severities: drop the pick, apply the list, never retry
  const [severityRefusals, setSeverityRefusals] = useState<
    Record<string, Vero1Severity[]>
  >({});
  // the name twin: a 422 carries the row's configured names at that severity
  const [nameRefusals, setNameRefusals] = useState<Record<string, string[]>>(
    {}
  );
  // the superseded draft's queue is disposed once a different id takes its
  // place; its buffers could only feed a review that no longer accepts them
  const prevChecklistIdRef = useRef<Key | null>(null);
  useEffect(() => {
    const prev = prevChecklistIdRef.current;
    if (
      prev !== null &&
      checklistId !== null &&
      String(prev) !== String(checklistId)
    ) {
      disposeVero1AutosaveQueue(prev);
      // the save lifecycle belongs to the draft it happened on — a carried
      // conflict would re-arm token adoption and banner the wrong review
      setRowSaveStates({});
      conflictTokens.current = {};
      setEngineToken(null);
      setSeverityRefusals({});
    }
    if (checklistId !== null) prevChecklistIdRef.current = checklistId;
  }, [checklistId]);
  useEffect(() => {
    if (!queue) return;
    queue.commitRef.current = editItemSlim;
    queue.lockedRef.current = Boolean(readOnly);
    queue.dealIdRef.current = dealId;
  });
  useEffect(() => {
    if (!queue) return undefined;
    queue.onBlockedRef.current = (code: Vero1ConflictCode) => {
      if (code === Vero1ConflictCode.DealDecisioned) setWriteRefused(true);
    };
    return () => {
      queue.onBlockedRef.current = null;
    };
  }, [queue]);
  useEffect(() => {
    if (!queue) return undefined;
    queue.onRejectedRef.current = (itemId, availableSeverities) => {
      setSeverityRefusals((prev) => ({
        ...prev,
        [itemId]: availableSeverities,
      }));
      setEditingItemId((current) =>
        current !== null && String(current) === itemId ? null : current
      );
      const key = VERO1_QUERY_KEYS.getVero1Checklist(dealId);
      queryCache.setQueryData(
        key,
        (prev: { checklist?: Vero1Checklist | null } | undefined) =>
          prev?.checklist
            ? {
                ...prev,
                checklist: {
                  ...prev.checklist,
                  items: prev.checklist.items.map((row) =>
                    String(row.id) === itemId
                      ? { ...row, availableSeverities }
                      : row
                  ),
                },
              }
            : prev
      );
      queryCache.invalidateQueries(key);
    };
    return () => {
      queue.onRejectedRef.current = null;
    };
  }, [queue, dealId]);
  useEffect(() => {
    if (!queue) return undefined;
    queue.onNameRejectedRef.current = (itemId, nameCandidates) => {
      setNameRefusals((prev) => ({ ...prev, [itemId]: nameCandidates }));
      setEditingItemId((current) =>
        current !== null && String(current) === itemId ? null : current
      );
      const key = VERO1_QUERY_KEYS.getVero1Checklist(dealId);
      queryCache.setQueryData(
        key,
        (prev: { checklist?: Vero1Checklist | null } | undefined) =>
          prev?.checklist
            ? {
                ...prev,
                checklist: {
                  ...prev.checklist,
                  items: prev.checklist.items.map((row) =>
                    String(row.id) === itemId
                      ? {
                          ...row,
                          nameCandidates: {
                            ...(row.nameCandidates ?? {}),
                            ...(row.overrideSeverity ?? row.originalSeverity
                              ? {
                                  [(row.overrideSeverity ??
                                    row.originalSeverity) as Vero1Severity]: nameCandidates,
                                }
                              : {}),
                          },
                        }
                      : row
                  ),
                },
              }
            : prev
      );
      queryCache.invalidateQueries(key);
    };
    return () => {
      queue.onNameRejectedRef.current = null;
    };
  }, [queue, dealId]);
  const clearNameRefusal = (itemId: string) =>
    setNameRefusals((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  const clearSeverityRefusal = (itemId: string) =>
    setSeverityRefusals((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  // per-row save lifecycle; the token captured at conflict time gates
  // Try-again, and a blocked write closes the open editor
  const [rowSaveStates, setRowSaveStates] = useState<
    Record<string, AutosaveEntitySnapshot>
  >({});
  const conflictTokens = useRef<Record<string, string | null>>({});
  useEffect(() => {
    if (!queue) return undefined;
    return queue.engine.subscribe((event) => {
      if (event.type !== "entity") return;
      if (event.status === "conflict") {
        conflictTokens.current[event.entityId] = queue.engine.getToken();
      }
      if (event.status === "blocked") {
        setEditingItemId(null);
      }
      setRowSaveStates((prev) => ({
        ...prev,
        [event.entityId]: { status: event.status, savedAt: event.savedAt },
      }));
    });
  }, [queue]);
  // token chaining: seeded once, advanced only by flush results — never
  // re-read from cache, so another agent's write 409s (§9.4). A conflict is
  // the one point the refetched token is adopted.
  const checklistToken = checklist?.updatedAt ?? null;
  const anyConflict = Object.values(rowSaveStates).some(
    (state) => state.status === "conflict"
  );
  // the bar at the step's foot carries the aggregate save state: Saving…
  // while anything is in flight, else the most recent landed save
  const footerSaving = Object.values(rowSaveStates).some(
    (state) => state.status === "saving" || state.status === "dirty"
  );
  const footerSavedAt = Object.values(rowSaveStates).reduce<number | null>(
    (max, state) =>
      state.savedAt !== null && (max === null || state.savedAt > max)
        ? state.savedAt
        : max,
    null
  );
  // the recommendation step's save state, surfaced in the sticky footer so
  // it is visible wherever the agent has scrolled
  const [reportSave, setReportSave] = useState<AutosaveEntitySnapshot>({
    status: "idle",
    savedAt: null,
  });
  useEffect(() => {
    if (checklistId === null || !onReportStep) return undefined;
    const reportQueue = getVero1ReportAutosaveQueue(checklistId);
    setReportSave(reportQueue.engine.statusOf(REPORT_ENTITY));
    return reportQueue.engine.subscribe((event) => {
      if (event.type === "entity" && event.entityId === REPORT_ENTITY) {
        setReportSave({ status: event.status, savedAt: event.savedAt });
      }
    });
  }, [checklistId, onReportStep]);
  // mirrored into state — Try-again re-enables on the adoption render
  const [engineToken, setEngineToken] = useState<string | null>(null);
  useEffect(() => {
    if (!queue) return;
    if (queue.engine.getToken() === null) {
      queue.engine.setToken(checklistToken);
    } else if (anyConflict) {
      queue.engine.adoptTokenIfNewer(checklistToken);
    }
    setEngineToken(queue.engine.getToken());
  }, [queue, checklistToken, anyConflict]);
  useEffect(() => {
    if (queue && readOnly) queue.engine.stop();
  }, [queue, readOnly]);
  // leaving the deal's pages drains through the queue, which outlives this
  useEffect(() => {
    if (!queue) return undefined;
    return () => {
      void queue.engine.drain();
    };
  }, [queue]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!queue?.engine.anyDirty()) return;
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = AUTOSAVE_STRINGS.unloadWarning;
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [queue]);

  const [gateDraining, setGateDraining] = useState(false);

  /**
   * A step change swaps the body's whole contents while PanelBody keeps its
   * scroll offset, so arriving at the report from a scrolled checklist lands
   * mid-form — past the recommendation the step exists to ask for. Each step
   * starts at its own top.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // instant, not smooth: the effect runs after the swap, so the new step is
    // already mounted — animating here would scroll THROUGH content the agent
    // never saw at the old offset. The step arrives at its top, cut.
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [onReportStep]);

  /** Brings a row to the middle of the body, the way an opening editor does. */
  const centreRow = useCallback((row: HTMLElement | null | undefined) => {
    const body = bodyRef.current;
    if (!body || !row) return;
    const top =
      row.getBoundingClientRect().top - body.getBoundingClientRect().top;
    const offset =
      body.scrollTop + top - body.clientHeight / 2 + row.offsetHeight / 2;
    body.scrollTo?.({ top: Math.max(0, offset), behavior: "smooth" });
  }, []);

  /**
   * An editor opens taller than the row it replaces, so a row near the fold
   * pushes its own fields below it — the agent opens an editor and has to go
   * looking for it. Centring the open row brings the whole card into view
   * without moving anything the agent did not just act on.
   *
   * Scrolls the body directly rather than scrollIntoView, which walks every
   * scrollable ancestor: the pane is one, and moving it is what the pin above
   * exists to undo.
   */
  useEffect(() => {
    if (editingItemId === null) return undefined;
    const body = bodyRef.current;
    if (!body || typeof requestAnimationFrame !== "function") return undefined;
    // after paint: the editor mounts in the same commit, so its height is not
    // measurable until the browser has laid it out
    const frame = requestAnimationFrame(() => {
      const editor = body.querySelector('[data-testid="vero1-row-editor"]');
      centreRow(
        editor?.closest<HTMLElement>('[data-testid="vero1-checklist-row"]')
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [editingItemId, centreRow]);

  const items = checklist?.items ?? [];
  const isDraft = checklist?.status === Vero1ChecklistStatus.Draft;

  // held restart: a 409'd start keeps the panel open and the draft starts
  // itself when the fresh run lands
  const generationState = generation?.state;
  // a Ready cached from before the re-screen kicked 409s on fire — hold
  // until generation moves off that snapshot
  const staleReadyRef = useRef(false);
  // the landing run changes several deps in one render window; two fires
  // both read isStartingChecklist=false and the loser 400s "already open"
  const autoStartInFlightRef = useRef(false);
  const generationRunId = generation?.latestRunId ?? null;
  useEffect(() => {
    // state left Ready, or a new run identity arrived (a re-screen can go
    // Ready -> Ready inside one poll gap — the run id never skips)
    staleReadyRef.current = false;
  }, [generationState, generationRunId]);
  useEffect(() => {
    if (!autoStartWhenReady || !visible) return;
    if (isDraft) {
      setAutoStartWhenReady(false);
      return;
    }
    // startPending = the band's own start (a different mutation instance) is
    // still in flight — firing here would double-start
    if (
      !startPending &&
      !staleReadyRef.current &&
      !autoStartInFlightRef.current &&
      generationState === Vero1GenerationStatus.Ready &&
      !isStartingChecklist &&
      !isChecklistLoading
    ) {
      autoStartInFlightRef.current = true;
      // NOT setStartPending: isStartingChecklist guards this instance, and
      // flipping the shared flag swaps the hold strip for a bare skeleton
      startChecklist(undefined, {
        // clearing before the result is known orphans the auto-start
        onSuccess: () => setAutoStartWhenReady(false),
        onError: (error: unknown) => {
          if (isRunInProgressConflict(error as never)) {
            // the re-screen is still running — stay armed for the poll
            staleReadyRef.current = true;
            return;
          }
          setAutoStartWhenReady(false);
        },
        onSettled: () => {
          autoStartInFlightRef.current = false;
        },
      });
    }
  }, [
    autoStartWhenReady,
    visible,
    isDraft,
    generationState,
    // Ready -> Ready re-fires via the run identity
    generationRunId,
    isStartingChecklist,
    isChecklistLoading,
    startPending,
    setAutoStartWhenReady,
    setStartPending,
    startChecklist,
  ]);
  // one settle for the whole start journey — per-mutation windows nest
  // inside it, so the banner never resolves to the outgoing status mid-way
  const journeySettleRef = useRef<Key | null>(null);
  const journeyActive = Boolean(startPending) || autoStartWhenReady;
  useEffect(() => {
    if (journeyActive && journeySettleRef.current === null) {
      journeySettleRef.current = dealId;
      enterVero1Settling(dealId);
    } else if (!journeyActive && journeySettleRef.current !== null) {
      exitVero1Settling(journeySettleRef.current);
      journeySettleRef.current = null;
    }
  }, [journeyActive, dealId]);
  useEffect(
    () => () => {
      // the ref holds the deal the window opened on; dealId may be cleared by now
      if (journeySettleRef.current !== null) {
        exitVero1Settling(journeySettleRef.current);
        journeySettleRef.current = null;
      }
    },
    []
  );

  // a submitted/set-aside record renders an empty body here — the record
  // surface is the deal review tab, so close instead of sitting blank
  const checklistStatus = checklist?.status;
  useEffect(() => {
    if (
      visible &&
      !isChecklistLoading &&
      !startPending &&
      !isStartingChecklist &&
      !autoStartWhenReady &&
      !onReportStep &&
      (checklistStatus === Vero1ChecklistStatus.Submitted ||
        checklistStatus === Vero1ChecklistStatus.NeedsReReview)
    ) {
      (onExit ?? onClose)();
    }
  }, [
    visible,
    isChecklistLoading,
    startPending,
    isStartingChecklist,
    autoStartWhenReady,
    onReportStep,
    checklistStatus,
    onExit,
    onClose,
  ]);

  // the save reassurance and forward action belong to an open draft; after
  // submit they would claim nothing has reached leasing when it has
  const showDraftFooter =
    Boolean(checklist) && isDraft && !readOnly && !onReportStep;
  const isDiscardableDraft = Boolean(checklist) && isDraft && !readOnly;
  const editedItems = items.filter(isLeasingTouched);
  const editedCount = editedItems.length;
  const incompleteItems = items.filter(isIncomplete);
  // which row Continue should carry the agent to — the first incomplete row
  const blockedBy = incompleteItems.length > 0 ? incompleteItems[0].id : null;
  // spec 3.3 breakdown: a changed result/value is a different act than an
  // annotation — the strip says which kind of activity happened
  const resultsChanged = editedItems.filter(hasResultMoved).length;
  const annotatedOnly = editedCount - resultsChanged;

  // two identical labels with possibly opposite verdicts are unreadable —
  // once rows span more than one application, every per-applicant row is
  // tagged with its applicant's name
  const multiApplicant =
    _.uniq(
      items
        .filter((item) => item.application !== null)
        .map((item) => String(item.application))
    ).length > 1;

  // rows arrive flat; the panel groups them by category to match the
  // category-card taxonomy the editor is specced against. Custom rows sit in
  // their own trailing section (spec 5.4) — the affordance reads as things
  // VERO1 added, not a change to Lumen's template
  const customItems = orderRowsInGroup(
    items.filter((item) => item.itemType === "CUSTOM"),
    { by: "effective" }
  );
  const grouped = _.chain(items)
    .filter((item) => item.itemType !== "CUSTOM")
    .groupBy((item) => item.category || "Other")
    .toPairs()
    .sortBy(([category]) => categorySortKey(category))
    .map(
      ([category, rows]) =>
        [category, orderRowsInGroup(rows, { by: "lumen" })] as const
    )
    .value();

  const renderBody = () => {
    if (isChecklistLoading || startPending) {
      // canvas 6.1: draw the shape of the rows that are coming, so the panel
      // doesn't reflow when the checklist lands
      return <ChecklistSkeleton testId="vero1-skeleton" />;
    }

    if (onReportStep && checklist && !isDraft && !readOnly) {
      return <ChecklistSkeleton testId="vero1-submitting" />;
    }

    if (onReportStep && checklist && isDraft && !readOnly) {
      return (
        <ReviewReportStep
          dealId={dealId}
          checklist={checklist}
          onCentre={centreRow}
          onBack={() => setOnReportStep(false)}
          onSubmitted={() => {
            setOnReportStep(false);
            onClose();
          }}
        />
      );
    }

    // Spec §6.1: the panel has no nothing-to-review case — it always opens on
    // a checklist. The trigger opens the panel and generates in parallel, so a
    // null checklist in a ready state means generation is in flight — draw the
    // skeleton, same as the query wait, so every load state looks the same.
    if (!checklist) {
      const state = generation?.state;
      const rescreening =
        state === Vero1GenerationStatus.RescreeningForReview;
      if (rescreening || state === Vero1GenerationStatus.RunInProgress) {
        // spec 6.3's grammar: a strip up top like the panel's other alerts,
        // spinner riding it, the shape of the coming rows underneath
        return (
          <>
            <ScreeningStrip data-testid="vero1-empty-state">
              <ScreeningSpinHolder>
                <SpinnerGlyph size={15} />
              </ScreeningSpinHolder>
              <div>
                <ScreeningTitle>
                  {rescreening
                    ? "Re-running screening for the freshest data"
                    : "Lumen is still screening this deal"}
                </ScreeningTitle>
                <ScreeningDetail>
                  {rescreening
                    ? "The review opens as soon as it lands"
                    : "Results appear together when it completes"}
                </ScreeningDetail>
              </div>
            </ScreeningStrip>
            <ChecklistSkeleton testId="vero1-skeleton" />
          </>
        );
      }
      if (state === Vero1GenerationStatus.LumenUnavailable) {
        return (
          <EmptyState data-testid="vero1-empty-state">
            <RegularText extraStrong>Lumen results are unavailable</RegularText>
            <Spacer size={Spacer.SIZES.sm} />
            <SmallText weak>
              We couldn&apos;t reach screening for this deal. Close this and try
              again shortly.
            </SmallText>
          </EmptyState>
        );
      }
      if (state === Vero1GenerationStatus.NoLumenConfig) {
        return (
          <EmptyState data-testid="vero1-empty-state">
            <RegularText extraStrong>No Lumen configuration</RegularText>
            <Spacer size={Spacer.SIZES.sm} />
            <SmallText weak>
              This property has no Lumen configuration yet. Ask an admin to
              assign a screening formula before a review can start.
            </SmallText>
          </EmptyState>
        );
      }
      return <ChecklistSkeleton testId="vero1-skeleton" />;
    }

    // a restart is waiting on the re-screen: hold with the screening copy
    // rather than rendering the set-aside review it is about to replace
    if (autoStartWhenReady && !isDraft) {
      return (
        <>
          <ScreeningStrip data-testid="vero1-restart-hold">
            <ScreeningSpinHolder>
              <SpinnerGlyph size={15} />
            </ScreeningSpinHolder>
            <div>
              {/* this path is only reached from the agent's own restart, so it
                  names their action rather than an external delay */}
              <ScreeningTitle>
                Re-running screening for the freshest data
              </ScreeningTitle>
              <ScreeningDetail>
                The review opens as soon as it lands. Discarding it restores the
                previous review if nothing changed.
              </ScreeningDetail>
            </div>
          </ScreeningStrip>
          <ChecklistSkeleton testId="vero1-restart-skeleton" />
        </>
      );
    }

    if (
      checklist.status === Vero1ChecklistStatus.Submitted ||
      checklist.status === Vero1ChecklistStatus.NeedsReReview
    ) {
      return null;
    }

    const rated = items.filter(isAssessable);
    const assessed = rated.filter((item) => item.originalSeverity !== null);
    const failing = assessed.filter(
      (item) =>
        item.originalSeverity === "fail" || item.originalSeverity === "warn"
    );
    const allFail = failing.every((item) => item.originalSeverity === "fail");
    // banner tone follows the canonical outcome — never the client-configurable
    // label text. No recognized outcome → no banner to orient to.
    const verdictTone = (
      {
        APPROVE: "approve",
        CONDITIONAL: "conditional",
        DECLINE: "decline",
      } as const
    )[checklist.originalOutcome as string];
    // decline/conditional name the failing rows driving the verdict
    // ("2 of 12 items fail · income ratio and credit score")
    const failingLabelList = failing
      .slice(0, 3)
      .map((item) => item.label.toLowerCase());
    if (failing.length > 3) failingLabelList.push("…");
    const failingLabels =
      failingLabelList.length > 1
        ? `${failingLabelList.slice(0, -1).join(", ")} and ${
            failingLabelList[failingLabelList.length - 1]
          }`
        : failingLabelList[0] ?? "";
    const failingCategories = _.uniq(
      failing.map((item) => formatCategory(item.category || "Other"))
    );

    const renderItem = (row: Vero1ChecklistItem) => {
      const refused = severityRefusals[String(row.id)];
      const nameRefused = nameRefusals[String(row.id)];
      const withSeverities = refused
        ? { ...row, availableSeverities: refused }
        : row;
      const refusedAt =
        withSeverities.overrideSeverity ?? withSeverities.originalSeverity;
      const item =
        nameRefused && refusedAt
          ? {
              ...withSeverities,
              nameCandidates: {
                ...(withSeverities.nameCandidates ?? {}),
                [refusedAt]: nameRefused,
              },
            }
          : withSeverities;
      // readOnly and isDraft are checked BEFORE the open editor: a deal
      // decisioned, or a review submitted elsewhere, would otherwise keep
      // collecting edits the API now refuses — the exact work this stops
      return editingItemId === item.id &&
        !readOnly &&
        isDraft &&
        queue !== null ? (
        // the row stays on screen around its editor (spec §09): header wears
        // the EDITING tag, the card renders beneath Lumen's reasoning
        <ChecklistRow
          key={item.id}
          item={item}
          editing
          editConflict={rowSaveStates[String(item.id)]?.status === "conflict"}
          severityRefused={refused}
          nameRefused={nameRefused}
          showApplicant={multiApplicant}
        >
          <ChecklistRowEditor
            item={item}
            reasonCatalog={reasonCatalog}
            onCentre={centreRow}
            saveState={
              rowSaveStates[String(item.id)] ?? {
                status: "idle",
                savedAt: null,
              }
            }
            conflict={rowSaveStates[String(item.id)]?.status === "conflict"}
            conflictRefreshing={
              rowSaveStates[String(item.id)]?.status === "conflict" &&
              engineToken === conflictTokens.current[String(item.id)]
            }
            onFieldEdit={(edit, opts) => {
              if ("overrideSeverity" in edit) {
                clearSeverityRefusal(String(item.id));
                clearNameRefusal(String(item.id));
              }
              if ("overrideOutcomeName" in edit) {
                clearNameRefusal(String(item.id));
              }
              queue.engine.stage(
                String(item.id),
                edit as Partial<Vero1AutosaveFields>
              );
              if (opts?.commit) void queue.engine.flush(String(item.id));
            }}
            onDropPending={(fields) =>
              queue.engine.drop(String(item.id), fields)
            }
            onDone={() => {
              void queue.engine.flush(String(item.id));
              const doneId = item.id;
              setEditingItemId(null);
              // bring the just-collapsed row into view so its state — an amber
              // Needs-findings / Needs-reason marker in particular — is on
              // screen, not left off the fold where the tall editor had pushed
              // the page. After paint: the row re-lays-out as the summary this
              // same commit.
              if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(() => {
                  const body = bodyRef.current;
                  centreRow(
                    body?.querySelector<HTMLElement>(
                      `[data-vero1-item="${doneId}"]`
                    )
                  );
                });
              }
            }}
            onRetryFlush={() => void queue.engine.retry(String(item.id))}
            onReloadRow={() => {
              queue.engine.drop(String(item.id));
              setEditingItemId(null);
            }}
          />
        </ChecklistRow>
      ) : (
        <ChecklistRow
          key={item.id}
          item={item}
          showApplicant={multiApplicant}
          readOnly={readOnly || !isDraft}
          saveConflict={rowSaveStates[String(item.id)]?.status === "conflict"}
          saveFailed={rowSaveStates[String(item.id)]?.status === "failed"}
          saveRetrying={rowSaveStates[String(item.id)]?.status === "saving"}
          severityRefused={refused}
          nameRefused={nameRefused}
          conflictRefreshing={
            rowSaveStates[String(item.id)]?.status === "conflict" &&
            engineToken === conflictTokens.current[String(item.id)]
          }
          onRetryFlush={() => void queue?.engine.retry(String(item.id))}
          onEdit={(next) => setEditingItemId(next.id)}
          onRemove={
            item.itemType === "CUSTOM" &&
            !item.fromTemplate &&
            !readOnly &&
            isDraft &&
            canAddField &&
            editingItemId === null
              ? (next) => setRemovingItemId(next.id)
              : undefined
          }
          removing={removingItemId === item.id}
          removeBusy={isDeletingItem}
          onConfirmRemove={() => {
            // drop the gone row's buffer + chain the delete token (no false 409)
            queue?.engine.drop(String(item.id));
            deleteItem(item.id, {
              onSuccess: (checklist: Vero1Checklist) =>
                queue?.engine.adoptTokenIfNewer(checklist?.updatedAt),
              onSettled: () => setRemovingItemId(null),
            });
          }}
          onCancelRemove={() => setRemovingItemId(null)}
        />
      );
    };

    return (
      <>
        {/* Say the deal is decided before anything else: from here the API
            refuses every write. Two tones — a draft was interrupted mid-work
            (controls that quietly stopped working need a reason), while a
            finished review opened afterwards is simply the record. */}
        {(writeRefused ||
          generation?.state === Vero1GenerationStatus.DealDecisioned) && (
          <DecisionedBanner data-testid="vero1-decisioned-banner">
            <Icon.WarningIcon />
            <p>
              {isDraft
                ? "Leasing decided this deal while your review was open. It is now read-only — anything you had entered is kept on the rows, but no further edits can be saved."
                : "This deal has been decisioned. This review is the record of it, read-only."}
            </p>
          </DecisionedBanner>
        )}
        {/* Orientation on every outcome, color-coded (August docs — reverses
            the 08-17 approve-only rule): approve names where risk hides so
            rubber-stamping doesn't; decline/conditional name the failing rows
            driving the verdict. */}
        {!checklist.finalRecommendation && verdictTone && (
          <>
            <LumenSummaryBlock
              $tone={verdictTone}
              data-testid="vero1-lumen-summary"
            >
              <VerdictCircle $tone={verdictTone}>
                {verdictTone === "approve" ? (
                  <TickGlyph size={12} strokeWidth={3.2} />
                ) : verdictTone === "decline" ? (
                  <CrossGlyph size={10} strokeWidth={3.2} />
                ) : (
                  <BangGlyph />
                )}
              </VerdictCircle>
              <div>
                <LumenSummaryTitle>
                  Lumen recommends{" "}
                  {verdictTone === "approve"
                    ? "approve"
                    : verdictTone === "decline"
                    ? "decline"
                    : "conditional"}
                </LumenSummaryTitle>
                <LumenSummaryDetail>
                  {verdictTone === "approve" ? (
                    <>
                      {assessed.length - failing.length} of {assessed.length}{" "}
                      {assessed.length === rated.length
                        ? "items pass"
                        : "assessed items pass"}
                      {failing.length === 0
                        ? " · no policy exceptions"
                        : ` · ${failingCategories
                            .join(", ")
                            .toLowerCase()} flagged`}
                    </>
                  ) : (
                    <>
                      {failing.length} of {assessed.length}{" "}
                      {assessed.length === rated.length
                        ? "items"
                        : "assessed items"}{" "}
                      {allFail ? "fail" : "flagged"}
                      {failingLabels && ` · ${failingLabels}`}
                    </>
                  )}
                  {assessed.length < rated.length &&
                    ` · ${rated.length - assessed.length} awaiting your review`}
                </LumenSummaryDetail>
              </div>
            </LumenSummaryBlock>
          </>
        )}
        {/* Activity, not progress: nothing is required, so nothing is
            counted toward a total (spec §3.3). */}
        {/* re-review invitation: a prior submitted review exists and none of
            its assessments have been adopted yet. Changed rows are excluded
            by the server — stale judgement never auto-applies to moved data */}
        {isDraft &&
          !readOnly &&
          checklist.predecessor != null &&
          !carryForwardDismissed &&
          !items.some((item) => item.adoptedFromVersion !== null) && (
            <CarryForwardBanner data-testid="vero1-carry-forward-banner">
              <SmallText>
                This is a re-review. Carry forward your previous review&apos;s
                assessments? Rows whose data changed stay open for fresh review.
              </SmallText>
              <FlexContainer justifyContent="flex-end" gap="8px">
                <PanelSecondaryButton
                  onClick={() => setCarryForwardDismissed(true)}
                >
                  Not now
                </PanelSecondaryButton>
                <PanelPrimaryButton
                  loading={isCarryingForward}
                  onClick={() =>
                    carryForward(undefined, {
                      onSuccess: () => setCarryForwardDismissed(true),
                    })
                  }
                >
                  Carry forward
                </PanelPrimaryButton>
              </FlexContainer>
            </CarryForwardBanner>
          )}
        {editedCount === 0 && isDraft && (
          <ActivityStrip data-testid="vero1-activity-strip-empty">
            <ActivityStripDetail>
              Nothing edited yet. Leaving an item alone accepts Lumen&apos;s
              result.
            </ActivityStripDetail>
          </ActivityStrip>
        )}
        {editedCount > 0 && (
          <ActivityStrip data-testid="vero1-activity-strip">
            <RailDot />
            <span>
              {editedCount} {editedCount === 1 ? "item" : "items"} edited{" "}
              <ActivityStripDetail>
                {"· "}
                {[
                  resultsChanged > 0 &&
                    `${resultsChanged} ${
                      resultsChanged === 1 ? "result" : "results"
                    } changed`,
                  annotatedOnly > 0 && `${annotatedOnly} annotated`,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </ActivityStripDetail>
            </span>
          </ActivityStrip>
        )}

        {grouped.map(([category, categoryItems]) => (
          <div key={category}>
            <CategoryLabel>{formatCategory(category)}</CategoryLabel>
            <Spacer size={Spacer.SIZES.xs} />
            {categoryItems.map(renderItem)}
            <Spacer size={Spacer.SIZES.sm} />
          </div>
        ))}
        {(customItems.length > 0 ||
          (isDraft && !readOnly && canAddField)) && (
          <div data-testid="vero1-additions-section">
            <CategoryLabel>VERO1 additions</CategoryLabel>
            <div>
              <SmallText weak>
                Items Lumen didn&apos;t assess. Anything you add is visible to
                leasing.
              </SmallText>
            </div>
            <Spacer size={Spacer.SIZES.xs} />
            {customItems.map(renderItem)}
            {isDraft && !readOnly && canAddField && (
              <AddChecklistItem
                saving={isAddingItem}
                scopeOptions={persistScopeOptions}
                onAdd={addItem}
                onOpen={() => {
                  // the form expands taller than the "+ Add a field" link it
                  // replaces, at the very bottom of the list — centre the
                  // additions section so the whole form is on screen
                  if (typeof requestAnimationFrame === "function") {
                    requestAnimationFrame(() => {
                      const body = bodyRef.current;
                      centreRow(
                        body?.querySelector<HTMLElement>(
                          '[data-testid="vero1-add-item-form"]'
                        )
                      );
                    });
                  }
                }}
              />
            )}
          </div>
        )}
      </>
    );
  };

  const confirmDiscard = () => {
    // close first — the awaited refresh otherwise flashes an
    // empty panel
    setConfirmingDiscard(false);
    // null the cached draft so the rail drops with the panel;
    // a failed discard restores it below
    const previous = queryCache.getQueryData(
      VERO1_QUERY_KEYS.getVero1Checklist(dealId)
    );
    queryCache.setQueryData(
      VERO1_QUERY_KEYS.getVero1Checklist(dealId),
      (prev: unknown) => ({
        ...((prev as object) || {}),
        checklist: null,
        reviewInProgress: false,
      })
    );
    (onExit ?? onClose)();
    discardChecklist(undefined, {
      onSuccess: () => {
        if (checklistId !== null) {
          disposeVero1AutosaveQueue(checklistId);
        }
      },
      onError: () => {
        // the draft still blocks the decision — restore and reopen
        if (previous !== undefined) {
          queryCache.setQueryData(
            VERO1_QUERY_KEYS.getVero1Checklist(dealId),
            previous
          );
        }
        queryCache.invalidateQueries(
          VERO1_QUERY_KEYS.getVero1Checklist(dealId)
        );
        onDiscardFailed();
      },
    });
  };

  const renderDiscardConfirm = (confirmTestId?: string) => (
    <>
      <SmallText weak>
        Discarding releases the leasing-decision block and is recorded.
        Nothing you entered will reach leasing.
      </SmallText>
      <Spacer size={Spacer.SIZES.xs} />
      <FlexContainer justifyContent="flex-end" flexWrap="wrap" gap="12px">
        <Button type="secondary" onClick={() => setConfirmingDiscard(false)}>
          Keep editing
        </Button>
        <Button
          type="primary"
          loading={isDiscardingChecklist}
          data-testid={confirmTestId}
          onClick={confirmDiscard}
        >
          Discard review
        </Button>
      </FlexContainer>
    </>
  );

  return (
    // hidden (not unmounted) when minimized so an in-flight edit survives the
    // rail round-trip
    <DockedPane
      ref={paneRef}
      $width={width}
      $visible={visible}
      data-testid="vero1-review-panel"
    >
      <ResizeGrip
        data-testid="vero1-resize-grip"
        aria-label="Resize panel"
        onPointerDown={startResize}
      />
      <PanelHeader>
        <FlexContainer
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap="8px"
        >
          <PanelTitle>VERO1 review</PanelTitle>
          <FlexContainer alignItems="center" gap="8px">
            {isChecklistLoading ? (
              <DraftBadge data-testid="vero1-loading-badge">Loading</DraftBadge>
            ) : (
              isDraft && <DraftBadge>Draft</DraftBadge>
            )}
            <CloseButton
              type="button"
              data-testid="vero1-minimize"
              aria-label="Minimize review panel"
              onClick={() => {
                void queue?.engine.flushAll();
                onClose();
              }}
            >
              <ChevronRightGlyph />
            </CloseButton>
          </FlexContainer>
        </FlexContainer>
        <PanelAttribution data-testid="vero1-panel-deal-label">
          {dealLabel || `Deal #${dealId}`}
          {checklist && checklist.startedByName && (
            <span data-testid="vero1-panel-attribution">
              {` · ${checklist.startedByName} · started ${formatReviewTime(
                checklist.createdAt
              )}`}
              {checklist.version > 1 && ` · version ${checklist.version}`}
            </span>
          )}
          {checklist && !checklist.startedByName && (
            <span data-testid="vero1-panel-attribution">
              {` · started ${formatReviewTime(checklist.createdAt)}`}
              {checklist.version > 1 && ` · version ${checklist.version}`}
            </span>
          )}
        </PanelAttribution>
      </PanelHeader>

      {clearedEntries.length > 0 && (
        <PanelNoticeSlot>
          <RefreshClearedNotice
            entries={clearedEntries}
            onDismiss={dismissClearedNotice}
          />
        </PanelNoticeSlot>
      )}
      <PanelBody ref={bodyRef} data-testid="vero1-panel-body">
        <Vero1ErrorBoundary
          context="vero1_review_panel"
          dealId={dealId}
          checklistId={checklistId ?? undefined}
          onErrorStateChange={setBodyErrored}
        >
          <PanelBodyContent render={renderBody} />
        </Vero1ErrorBoundary>
      </PanelBody>

      {bodyErrored ? (
        isDiscardableDraft && (
          <PanelFooter>
            {confirmingDiscard ? (
              renderDiscardConfirm("vero1-errored-discard-confirm")
            ) : (
              <FooterRow>
                <FooterHelper>
                  This review couldn&apos;t load. Retry above, or discard the
                  draft to release the leasing-decision block.
                  <div>
                    <DiscardLink
                      type="button"
                      onClick={() => setConfirmingDiscard(true)}
                    >
                      Discard draft
                    </DiscardLink>
                  </div>
                </FooterHelper>
              </FooterRow>
            )}
          </PanelFooter>
        )
      ) : (
      <PanelFooter>
        {showDraftFooter && confirmingDiscard ? (
          renderDiscardConfirm()
        ) : onReportStep && checklist && isDraft && !readOnly ? (
          <FooterRow>
            <FooterHelper>
              {AUTOSAVE_STRINGS.footer}{" "}
              {(reportSave.status === "saving" ||
                reportSave.status === "dirty" ||
                reportSave.savedAt !== null) && (
                <SaveStatus
                  data-testid="vero1-report-save-status"
                  style={{ marginLeft: 8 }}
                >
                  {reportSave.status === "saving" ||
                  reportSave.status === "dirty"
                    ? AUTOSAVE_STRINGS.saving
                    : AUTOSAVE_STRINGS.savedAt(
                        autosaveTimeLabel(reportSave.savedAt as number)
                      )}
                </SaveStatus>
              )}
            </FooterHelper>
          </FooterRow>
        ) : (
          showDraftFooter && (
            <FooterRow>
              <FooterHelper>
                {incompleteItems.length > 0
                  ? `${
                      incompleteItems.some(needsReason)
                        ? "Needs a reason or findings"
                        : "Needs findings"
                    } before the recommendation: ${incompleteItems
                      .map((item) => item.label)
                      .join(", ")}.`
                  : AUTOSAVE_STRINGS.footer}{" "}
                {(footerSaving || footerSavedAt !== null) && (
                  <SaveStatus
                    data-testid="vero1-checklist-save-status"
                    style={{ marginLeft: 8 }}
                  >
                    {footerSaving
                      ? AUTOSAVE_STRINGS.saving
                      : AUTOSAVE_STRINGS.savedAt(
                          autosaveTimeLabel(footerSavedAt as number)
                        )}
                  </SaveStatus>
                )}
                <div>
                  <DiscardLink
                    type="button"
                    onClick={() => setConfirmingDiscard(true)}
                  >
                    Discard draft
                  </DiscardLink>
                </div>
              </FooterHelper>
              {/* Blocked, but not inert: the footer says WHAT is wrong while
                  the row it names can be far up the list, so the click takes
                  the agent there instead of doing nothing. aria-disabled
                  rather than disabled — a disabled button fires no click and
                  leaves assistive tech no way to ask why. */}
              <PanelPrimaryButton
                type="primary"
                aria-disabled={blockedBy !== null}
                $muted={blockedBy !== null}
                loading={gateDraining}
                data-testid="vero1-continue"
                onClick={async () => {
                  // everything buffered lands before the gate reads the flags
                  setEditingItemId(null);
                  setGateDraining(true);
                  try {
                    if (queue) await queue.engine.drain();
                  } finally {
                    setGateDraining(false);
                  }
                  const fresh =
                    queryCache.getQueryData<{
                      checklist?: { items?: Vero1ChecklistItem[] } | null;
                    }>(VERO1_QUERY_KEYS.getVero1Checklist(dealId))?.checklist
                      ?.items ?? items;
                  const blocked = fresh.filter(isIncomplete);
                  if (blocked.length === 0) {
                    setOnReportStep(true);
                    return;
                  }
                  const body = bodyRef.current;
                  const row = body?.querySelector<HTMLElement>(
                    `[data-vero1-item="${blocked[0].id}"]`
                  );
                  centreRow(row);
                }}
              >
                Continue to recommendation
              </PanelPrimaryButton>
            </FooterRow>
          )
        )}
      </PanelFooter>
      )}
    </DockedPane>
  );
};
